import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import Title from "../../extra/Title";
import { apiInstanceFetch } from "../../../util/api";

const INK = "#1A1614";
const MUTED = "#8A857F";
const HAIRLINE = "#E8E1D3";
const GOLD = "#D4A129";
const CREAM = "#FFFDF7";
const RED = "#DC2626";
const GREEN = "#16A34A";

// Staff accounts for the standalone support console (ead.howyin.com/support-panel).
// Agents manage buyer support tickets there; this page only manages their accounts.
const CONSOLE_URL = "https://ead.howyin.com/support-panel/";

const emptyForm = { name: "", email: "", password: "" };

const SupportAgents = () => {
  const [agents, setAgents] = useState([]);
  const [busy, setBusy] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [resetFor, setResetFor] = useState(null); // agent object
  const [newPassword, setNewPassword] = useState("");

  const load = () =>
    apiInstanceFetch
      .get(`supportAgent/admin/list`)
      .then((r) => {
        if (r?.status) setAgents(r.agents || []);
      })
      .catch(() => {});

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const createAgent = () => {
    if (!form.name.trim() || !form.email.trim() || form.password.length < 8) {
      alert("Name, email and a password of at least 8 characters are required");
      return;
    }
    setBusy("create");
    apiInstanceFetch
      .post(`supportAgent/admin/create`, {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
      })
      .then((r) => {
        if (r?.status) {
          setShowCreate(false);
          setForm(emptyForm);
          load();
        } else alert(r?.message || "Could not create agent");
      })
      .catch(() => alert("Could not create agent"))
      .finally(() => setBusy(""));
  };

  const toggleActive = (agent) => {
    setBusy(agent._id);
    apiInstanceFetch
      .post(`supportAgent/admin/setActive`, { agentId: agent._id, active: !agent.active })
      .then((r) => {
        if (r?.status)
          setAgents((prev) =>
            prev.map((a) => (a._id === agent._id ? { ...a, active: !agent.active } : a))
          );
        else alert(r?.message || "Action failed");
      })
      .catch(() => alert("Action failed"))
      .finally(() => setBusy(""));
  };

  const resetPassword = () => {
    if (newPassword.length < 8) {
      alert("New password must be at least 8 characters");
      return;
    }
    setBusy(resetFor._id);
    apiInstanceFetch
      .post(`supportAgent/admin/resetPassword`, { agentId: resetFor._id, password: newPassword })
      .then((r) => {
        if (r?.status) {
          setResetFor(null);
          setNewPassword("");
          alert("Password updated");
        } else alert(r?.message || "Reset failed");
      })
      .catch(() => alert("Reset failed"))
      .finally(() => setBusy(""));
  };

  return (
    <div className="mainCategory">
      <Title name="Support Agents" />

      {/* Staff console pointer */}
      <div
        style={{
          background: CREAM,
          border: `1px solid ${GOLD}`,
          borderLeft: `5px solid ${GOLD}`,
          borderRadius: 12,
          padding: "14px 18px",
          margin: "16px 0",
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <i className="fa-solid fa-headset" style={{ color: GOLD, fontSize: 20 }} />
        <div>
          <div style={{ fontWeight: 700, color: INK }}>
            Staff console:{" "}
            <a href={CONSOLE_URL} target="_blank" rel="noreferrer" style={{ color: GOLD }}>
              {CONSOLE_URL}
            </a>
          </div>
          <div style={{ fontSize: 12.5, color: MUTED }}>
            Agents sign in there with the credentials you create on this page.
          </div>
        </div>
        <button
          className="btn btn-sm"
          style={{ marginLeft: "auto", background: INK, color: "#fff", fontWeight: 700 }}
          onClick={() => {
            setForm(emptyForm);
            setShowCreate(true);
          }}
        >
          <i className="fa-solid fa-plus" style={{ marginRight: 6 }} />
          New Agent
        </button>
      </div>

      {agents.length === 0 ? (
        <div style={{ background: "#fff", border: `1px solid ${HAIRLINE}`, borderRadius: 14, padding: 48, textAlign: "center", color: MUTED }}>
          <i className="fa-solid fa-headset" style={{ fontSize: 34, color: HAIRLINE }} />
          <div style={{ marginTop: 12, fontWeight: 600 }}>No support agents yet</div>
          <div style={{ fontSize: 12.5 }}>Create the first account with the "New Agent" button.</div>
        </div>
      ) : (
        <div style={{ background: "#fff", border: `1px solid ${HAIRLINE}`, borderRadius: 14, overflow: "auto" }}>
          <table className="table align-middle" style={{ margin: 0, minWidth: 760 }}>
            <thead>
              <tr style={{ background: CREAM }}>
                {["Name", "Email", "Status", "Last Login", "Created", "Action"].map((h) => (
                  <th key={h} style={{ fontSize: 11, textTransform: "uppercase", color: MUTED, padding: "10px 12px", borderBottom: `2px solid ${HAIRLINE}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <tr key={a._id}>
                  <td style={{ padding: "8px 12px", fontWeight: 600, color: INK }}>{a.name}</td>
                  <td style={{ padding: "8px 12px" }}>{a.email}</td>
                  <td style={{ padding: "8px 12px" }}>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "3px 10px",
                        borderRadius: 999,
                        fontSize: 11.5,
                        fontWeight: 700,
                        background: a.active ? "#E7F6EC" : "#FDECEC",
                        color: a.active ? GREEN : RED,
                      }}
                    >
                      {a.active ? "Active" : "Disabled"}
                    </span>
                  </td>
                  <td style={{ padding: "8px 12px", whiteSpace: "nowrap", color: MUTED }}>
                    {a.lastLoginAt ? dayjs(a.lastLoginAt).format("DD MMM YYYY HH:mm") : "Never"}
                  </td>
                  <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>{dayjs(a.createdAt).format("DD MMM YYYY")}</td>
                  <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        disabled={busy === a._id}
                        onClick={() => toggleActive(a)}
                        className="btn btn-sm"
                        style={{
                          background: a.active ? "#FDECEC" : "#E7F6EC",
                          color: a.active ? RED : GREEN,
                          fontWeight: 700,
                          border: "none",
                        }}
                      >
                        {a.active ? "Deactivate" : "Activate"}
                      </button>
                      <button
                        disabled={busy === a._id}
                        onClick={() => {
                          setResetFor(a);
                          setNewPassword("");
                        }}
                        className="btn btn-sm"
                        style={{ background: CREAM, color: INK, fontWeight: 700, border: `1px solid ${HAIRLINE}` }}
                      >
                        Reset Password
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      {showCreate ? (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1050 }}>
          <div style={{ background: "#fff", borderRadius: 14, padding: 22, width: 420, maxWidth: "92%" }}>
            <h5 style={{ marginTop: 0 }}>New support agent</h5>
            <p style={{ fontSize: 12.5, color: MUTED }}>
              Share the email and password with the agent — they sign in at the staff console.
            </p>
            <input
              className="form-control mb-2"
              placeholder="Full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <input
              className="form-control mb-2"
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <input
              className="form-control"
              type="text"
              placeholder="Password (min 8 characters)"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
            <div className="d-flex justify-content-end" style={{ gap: 8, marginTop: 14 }}>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
              <button
                className="btn btn-sm"
                style={{ background: INK, color: "#fff", fontWeight: 700 }}
                disabled={busy === "create" || !form.name.trim() || !form.email.trim() || form.password.length < 8}
                onClick={createAgent}
              >
                Create agent
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Reset-password modal */}
      {resetFor ? (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1050 }}>
          <div style={{ background: "#fff", borderRadius: 14, padding: 22, width: 420, maxWidth: "92%" }}>
            <h5 style={{ marginTop: 0 }}>Reset password</h5>
            <p style={{ fontSize: 12.5, color: MUTED }}>
              Set a new password for <b style={{ color: INK }}>{resetFor.name}</b> ({resetFor.email}).
            </p>
            <input
              className="form-control"
              type="text"
              placeholder="New password (min 8 characters)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <div className="d-flex justify-content-end" style={{ gap: 8, marginTop: 14 }}>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setResetFor(null)}>Cancel</button>
              <button
                className="btn btn-sm"
                style={{ background: INK, color: "#fff", fontWeight: 700 }}
                disabled={busy === resetFor._id || newPassword.length < 8}
                onClick={resetPassword}
              >
                Set new password
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SupportAgents;
